import { useState, useRef, useEffect } from "react";
import logo from "./assets/logo.webp";

const SEGMENTS = [
    { lines: ["Shaving /", "beard trim"] },
    { lines: ["Face de tan", "(men & women)"] },
    { lines: ["Threading", "(eyebrow", "upper lip)"] },
    { lines: ["Regular", "nail polish"] },
    { lines: ["Women hair", "wash blow dry"] },
    { lines: ["Feet", "massage"] },
    { lines: ["Men's hair wash", "& styling"] },
];

const N = SEGMENTS.length;
const SEG = (2 * Math.PI) / N;

// --- GOOGLE FORM CONFIGURATION ---
const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScyvsMZoGm1zxlLLdi7frrOUqnx3lxLAReIxccQk4HAbzgmyA/viewform";
const GOOGLE_FORM_ENTRY_ID = "entry.1869651051"; // Real Entry ID from user
// ---------------------------------

function drawWheel(canvas, angle) {
    const ctx = canvas.getContext("2d");
    const W = canvas.width,
        H = canvas.height;
    const cx = W / 2,
        cy = H / 2;
    const R = Math.min(cx, cy) - 8;

    ctx.clearRect(0, 0, W, H);

    // Outer gold border ring
    const ringGrad = ctx.createRadialGradient(cx, cy, R - 4, cx, cy, R + 10);
    ringGrad.addColorStop(0, "#5a3c05");
    ringGrad.addColorStop(0.3, "#d4af37");
    ringGrad.addColorStop(0.6, "#f0d060");
    ringGrad.addColorStop(1, "#8b6514");
    ctx.beginPath();
    ctx.arc(cx, cy, R + 10, 0, 2 * Math.PI);
    ctx.fillStyle = ringGrad;
    ctx.fill();

    // Segments
    SEGMENTS.forEach((seg, i) => {
        const isGold = i % 2 === 0;
        const sa = angle + i * SEG - Math.PI / 2;
        const ea = sa + SEG;
        const mid = sa + SEG / 2;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, R, sa, ea);
        ctx.closePath();

        if (isGold) {
            const gx = cx + Math.cos(mid) * R * 0.55;
            const gy = cy + Math.sin(mid) * R * 0.55;
            const g = ctx.createRadialGradient(gx, gy, 2, cx, cy, R);
            g.addColorStop(0, "#f8e878");
            g.addColorStop(0.45, "#d4af37");
            g.addColorStop(1, "#7a5505");
            ctx.fillStyle = g;
        } else {
            ctx.fillStyle = "#273527";
        }
        ctx.fill();

        // Gold divider lines
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, R, sa, ea);
        ctx.closePath();
        ctx.strokeStyle = "#d4af37";
        ctx.lineWidth = 2.5;
        ctx.stroke();

        // Text
        ctx.translate(cx, cy);
        ctx.rotate(mid);
        ctx.textAlign = "right";
        ctx.textBaseline = "middle";

        const lines = seg.lines;
        const lh = 12.5;
        const totalH = lines.length * lh;
        const textX = R * 0.87;

        lines.forEach((line, j) => {
            const y = -totalH / 2 + j * lh + lh / 2;
            const textColor = isGold ? "#1b2e0a" : "#d4af37";
            ctx.fillStyle = textColor;
            ctx.font = `600 10px 'Georgia', serif`;
            ctx.shadowColor = isGold
                ? "rgba(255,255,200,0.3)"
                : "rgba(0,0,0,0.5)";
            ctx.shadowBlur = 2;
            ctx.fillText(line, textX, y);
        });

        ctx.restore();
    });

    // Center hub gold
    const hubGrad = ctx.createRadialGradient(cx - 10, cy - 10, 3, cx, cy, 38);
    hubGrad.addColorStop(0, "#fff5a0");
    hubGrad.addColorStop(0.35, "#e8c830");
    hubGrad.addColorStop(0.7, "#b8900a");
    hubGrad.addColorStop(1, "#5a3c05");
    ctx.beginPath();
    ctx.arc(cx, cy, 38, 0, 2 * Math.PI);
    ctx.fillStyle = hubGrad;
    ctx.fill();
    ctx.strokeStyle = "#8b6914";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Inner dark ring
    ctx.beginPath();
    ctx.arc(cx, cy, 20, 0, 2 * Math.PI);
    ctx.fillStyle = "#0a0500";
    ctx.fill();
    ctx.strokeStyle = "#5a3c05";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Sheen dot
    ctx.beginPath();
    ctx.arc(cx - 7, cy - 7, 4, 0, 2 * Math.PI);
    ctx.fillStyle = "rgba(255,245,150,0.35)";
    ctx.fill();
}

function getWinnerIdx(angle) {
    const norm = ((-angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    return Math.floor(norm / SEG) % N;
}

export default function IshEssenceSpinWheel() {
    const canvasRef = useRef(null);
    const angleRef = useRef(0);
    const velRef = useRef(0);
    const rafRef = useRef(null);
    const [spinning, setSpinning] = useState(false);
    const [winner, setWinner] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [spun, setSpun] = useState(false);

    useEffect(() => {
        if (canvasRef.current) drawWheel(canvasRef.current, 0);
    }, []);

    const spin = () => {
        if (spinning) return;
        setSpinning(true);
        setShowModal(false);
        setWinner(null);
        setSpun(true);
        velRef.current = 0.22 + Math.random() * 0.18;

        const animate = () => {
            velRef.current *= 0.984;
            angleRef.current += velRef.current;
            if (canvasRef.current) drawWheel(canvasRef.current, angleRef.current);
            if (velRef.current > 0.004) {
                rafRef.current = requestAnimationFrame(animate);
            } else {
                setSpinning(false);
                const idx = getWinnerIdx(angleRef.current);
                const w = SEGMENTS[idx].lines.join(" ");
                setWinner(w);
                setTimeout(() => setShowModal(true), 400);
            }
        };
        rafRef.current = requestAnimationFrame(animate);
    };

    const reset = () => {
        setShowModal(false);
        setWinner(null);
        setSpun(false);
    };

    const handleClaim = () => {
        if (!winner) return;
        
        const prefilledUrl = `${GOOGLE_FORM_URL}?${GOOGLE_FORM_ENTRY_ID}=${encodeURIComponent(winner)}`;
        
        window.open(prefilledUrl, "_blank");
        setShowModal(false);
    };

    return (
        <div
            style={{
                minHeight: "100vh",
                background:
                    "radial-gradient(ellipse at 35% 15%, #2e4520 0%, #1a2d0d 25%, #0e1d07 55%, #070f04 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "Georgia, 'Times New Roman', serif",
                position: "relative",
                overflow: "hidden",
                padding: "20px 12px",
            }}
        >
            {/* Background bokeh */}
            {[...Array(8)].map((_, i) => (
                <div
                    key={i}
                    style={{
                        position: "absolute",
                        width: 80 + i * 30,
                        height: 80 + i * 30,
                        borderRadius: "50%",
                        background: `radial-gradient(circle, rgba(${30 + i * 5},${50 + i * 4},${20 + i * 3},0.3) 0%, transparent 70%)`,
                        left: `${[5, 70, 20, 85, 10, 60, 35, 90][i]}%`,
                        top: `${[10, 5, 60, 30, 80, 70, 15, 50][i]}%`,
                        transform: "translate(-50%,-50%)",
                        pointerEvents: "none",
                    }}
                />
            ))}

            <div
                style={{
                    width: "100%",
                    maxWidth: 400,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 6,
                    position: "relative",
                    zIndex: 1,
                    border: "1px solid rgba(212,175,55,0.18)",
                    borderRadius: 20,
                    padding: "28px 16px 32px",
                    background: "rgba(0,0,0,0.25)",
                    backdropFilter: "blur(6px)",
                }}
            >
                {/* Logo */}
                <div style={{ position: "relative", marginBottom: 2 }}>
                    <img 
                        src={logo} 
                        alt="Ish Essence Logo" 
                        style={{ 
                            width: 140, 
                            height: "auto",
                            filter: "brightness(0) invert(1) drop-shadow(0 0 8px rgba(255,255,255,0.3))",
                            opacity: 0.95
                        }} 
                    />
                </div>

                {/* Divider */}
                <div
                    style={{
                        width: 70,
                        height: 1,
                        background:
                            "linear-gradient(to right, transparent, rgba(212,175,55,0.6), transparent)",
                        margin: "2px 0",
                    }}
                />

                {/* Welcome text */}
                <div
                    style={{
                        color: "rgba(255,255,255,0.82)",
                        fontSize: 13.5,
                        letterSpacing: "0.09em",
                        marginTop: 6,
                        textAlign: "center",
                    }}
                >
                    Welcome to Ish Essence
                </div>
                <div
                    style={{
                        color: "#fff",
                        fontSize: 17,
                        fontWeight: 700,
                        letterSpacing: "0.025em",
                        textAlign: "center",
                    }}
                >
                    Something special awaits you
                </div>

                <div
                    style={{
                        width: 80,
                        height: 1,
                        background:
                            "linear-gradient(to right, transparent, rgba(212,175,55,0.35), transparent)",
                        margin: "6px 0",
                    }}
                />

                <div
                    style={{
                        color: "rgba(255,255,255,0.75)",
                        fontSize: 13,
                        letterSpacing: "0.07em",
                        textAlign: "center",
                    }}
                >
                    To celebrate our arrival in
                </div>
                <div
                    style={{
                        color: "#fff",
                        fontSize: 52,
                        fontWeight: 300,
                        letterSpacing: "-0.015em",
                        lineHeight: 1.05,
                        fontFamily: "Palatino Linotype, Palatino, serif",
                        textShadow: "0 2px 24px rgba(255,255,255,0.12)",
                        textAlign: "center",
                        margin: "0 0 2px",
                    }}
                >
                    Ahmedabad
                </div>

                <div
                    style={{
                        color: "#d4af37",
                        fontSize: 15,
                        fontWeight: 700,
                        letterSpacing: "0.03em",
                        textAlign: "center",
                        textShadow: "0 0 12px rgba(212,175,55,0.45)",
                        marginBottom: 6,
                        animation: "shimmer 2.5s ease-in-out infinite",
                    }}
                >
                    Spin the wheel to reveal your reward
                </div>

                {/* Wheel */}
                <div
                    style={{
                        position: "relative",
                        cursor: spinning ? "default" : "pointer",
                        userSelect: "none",
                    }}
                    onClick={!spinning ? spin : undefined}
                >
                    {/* Pointer teardrop */}
                    <div
                        style={{
                            position: "absolute",
                            top: -12,
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 10,
                        }}
                    >
                        <svg width="26" height="38" viewBox="0 0 26 38">
                            <defs>
                                <radialGradient id="pg" cx="35%" cy="28%" r="65%">
                                    <stop offset="0%" stopColor="#fffaaa" />
                                    <stop offset="40%" stopColor="#e8c830" />
                                    <stop offset="100%" stopColor="#7a5505" />
                                </radialGradient>
                            </defs>
                            <path
                                d="M13 2 C7 2 2 8 2 15 C2 24 13 36 13 36 C13 36 24 24 24 15 C24 8 19 2 13 2Z"
                                fill="url(#pg)"
                                stroke="#7a5505"
                                strokeWidth="1.2"
                            />
                            <circle cx="10" cy="11" r="2.5" fill="rgba(255,250,180,0.5)" />
                        </svg>
                    </div>

                    <canvas
                        ref={canvasRef}
                        width={300}
                        height={300}
                        style={{ display: "block", borderRadius: "50%" }}
                    />

                    {!spun && !spinning && (
                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                pointerEvents: "none",
                            }}
                        >
                            <div
                                style={{
                                    background: "rgba(212,175,55,0.9)",
                                    color: "#1a0e00",
                                    borderRadius: 20,
                                    padding: "8px 18px",
                                    fontSize: 13,
                                    fontWeight: 700,
                                    letterSpacing: "0.05em",
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                                }}
                            >
                                TAP TO SPIN
                            </div>
                        </div>
                    )}
                </div>

                {/* Claim button */}
                <button
                    onClick={!spinning ? (winner ? () => setShowModal(true) : spin) : undefined}
                    disabled={spinning}
                    style={{
                        marginTop: 14,
                        padding: "15px 0",
                        width: "100%",
                        maxWidth: 300,
                        background: "rgba(45,65,45,0.75)",
                        border: "1px solid rgba(212,175,55,0.35)",
                        borderRadius: 10,
                        color: "#fff",
                        fontSize: 18,
                        fontFamily: "Georgia, serif",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                        cursor: spinning ? "default" : "pointer",
                        opacity: spinning ? 0.6 : 1,
                        transition: "all 0.25s",
                    }}
                >
                    {spinning ? "Spinning..." : "Claim Your Reward"}
                </button>
            </div>

            {/* Result modal */}
            {showModal && winner && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.78)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 200,
                    }}
                    onClick={() => setShowModal(false)}
                >
                    <div
                        style={{
                            background: "linear-gradient(155deg, #1c2e0c, #2a3d18)",
                            border: "2px solid #d4af37",
                            borderRadius: 18,
                            padding: "36px 40px",
                            textAlign: "center",
                            maxWidth: 320,
                            margin: "0 20px",
                            boxShadow:
                                "0 0 60px rgba(212,175,55,0.2), 0 24px 60px rgba(0,0,0,0.6)",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ fontSize: 32, marginBottom: 10 }}>🎉</div>
                        <div
                            style={{
                                color: "#d4af37",
                                fontSize: 11,
                                letterSpacing: "0.15em",
                                marginBottom: 10,
                                opacity: 0.85,
                            }}
                        >
                            YOUR REWARD IS
                        </div>
                        <div
                            style={{
                                color: "#fff",
                                fontSize: 22,
                                fontWeight: 700,
                                marginBottom: 12,
                                fontFamily: "Georgia, serif",
                                lineHeight: 1.3,
                            }}
                        >
                            {winner}
                        </div>
                        <div
                            style={{
                                width: 60,
                                height: 1,
                                background:
                                    "linear-gradient(to right, transparent, #d4af37, transparent)",
                                margin: "0 auto 16px",
                            }}
                        />
                        <div
                            style={{
                                color: "rgba(255,255,255,0.6)",
                                fontSize: 12,
                                marginBottom: 22,
                                lineHeight: 1.6,
                            }}
                        >
                            Visit Ish Essence Ahmedabad to redeem your complimentary service!
                        </div>
                        <div
                            style={{ display: "flex", gap: 10, justifyContent: "center" }}
                        >
                            <button
                                onClick={reset}
                                style={{
                                    background: "transparent",
                                    border: "1px solid rgba(212,175,55,0.5)",
                                    borderRadius: 8,
                                    padding: "9px 18px",
                                    color: "#d4af37",
                                    fontWeight: 600,
                                    fontSize: 13,
                                    cursor: "pointer",
                                    fontFamily: "Georgia, serif",
                                }}
                            >
                                Spin Again
                            </button>
                            <button
                                onClick={handleClaim}
                                style={{
                                    background: "#d4af37",
                                    border: "none",
                                    borderRadius: 8,
                                    padding: "9px 18px",
                                    color: "#1a0e00",
                                    fontWeight: 700,
                                    fontSize: 13,
                                    cursor: "pointer",
                                    fontFamily: "Georgia, serif",
                                }}
                            >
                                Claim ✓
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        @keyframes shimmer {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.85); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.4); }
          50% { box-shadow: 0 0 0 12px rgba(212,175,55,0); }
        }
      `}</style>
        </div>
    );
}